import Head from "next/head";
import CLMHero2 from "components/CLM/CLMHero2";
import EqualDivisionSection from "components/CLM/EqualDivisionSection";
import { useInView } from "framer-motion";
import Image from "next/image";
import { useRef } from "react";
import { BASE_ASSETS_URL } from "@constants/constants";

const CLMHeroData = {
  // title: "Reporting",
  heading: "Edit, track, automate, and optimize contracts in one CLM",
  description:
    "Manage contracts Lifecycle from draft to renewal with built-in editing, tracking, automation, and analytics.",
  // image: `${BASE_ASSETS_URL}/v2/clm/clm/contract-management-hero.gif"),
  video: `${BASE_ASSETS_URL}/v2/clm/videos/contract-management-hero.mp4`,
  btnText: "Take a Quick Demo",
  btnUrl: "/clm/request-demo",
};

const ContractManagement = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, {
    once: false,
    amount: 0.5,
    // margin: "0px 0px -5% 0px",
  });

  return (
    <>
      <Head>
        <title>
          Contract Management Software | BoloSign CLM Contract Lifecycle
        </title>
        <meta
          name="description"
          content="BoloSign CLM is a contract management software that helps legal teams draft, review, automate, and optimize contracts faster with built-in contract lifecycle management."
        />
        <meta
          name="keywords"
          content="Contract management software, CLM software, contract lifecycle management, contract automation, contract tracking, contract analytics, BoloSign CLM"
        />
        <link rel="canonical" href="https://www.bolosign.com/clm/contract-lifecycle/" />
      </Head>
      <CLMHero2 CLMHeroData={CLMHeroData} />

      <EqualDivisionSection
        // title="Templates"
        heading="Edit, collaborate, and review contracts"
        description="Work in Microsoft Word’s interface, collaborate in real-time, and review with AI—all without leaving BoloSign."
        // image={`${BASE_ASSETS_URL}/v2/clm/clm/contract-management-edit.gif")}
        video={`${BASE_ASSETS_URL}/v2/clm/videos/contract-management-edit.mp4`}
        bgColor="bg-transparent"
        imageRight={true}
        checks={[
          "Edit in Word, Stay in BoloSign – Draft, edit, and save contracts with formatting and comments fully preserved.",
          "Collaborate in Real-Time – Co-edit with your team, track changes, and manage internal vs external comments.",
          "Review with AI – Use smart playbooks to automate checks, redlines, and policy compliance.",
        ]}
      />

      <section className="text-black px-6 md:px-8 mb-10 md:mb-28 mt-10 ">
        <div className="max-w-screen-xl xl:px-8 mx-auto flex flex-col md:flex-row gap-6 justify-between items-start">
          <div className="text-center flex flex-col items-center md:gap-3 mb-6 md:mb-0">
            <Image
              src={`${BASE_ASSETS_URL}/v2/clm/clm/contract-management-version.png`}
              alt="filter-by"
              width={300}
              height={300}
              className="w-full h-full rounded-2xl"
            />
            <div className="text-start">
              <p className="text-xl md:text-2xl">
                Draft, review, and store all contracts with full version control
              </p>
            </div>
          </div>

          <div className="text-center flex flex-col items-center md:gap-3 mb-6 md:mb-0">
            <Image
              src={`${BASE_ASSETS_URL}/v2/clm/clm/contract-management-monitor.gif`}
              alt="expiry-is"
              width={300}
              height={300}
              className="w-full h-full"
            />
            <div className="text-start">
              <p className="text-xl md:text-2xl">
                Monitor progress, spot delays, and share insights across teams
              </p>
            </div>
          </div>

          <div className="text-center flex flex-col items-center md:gap-3 mb-6 md:mb-0">
            <Image
              src={`${BASE_ASSETS_URL}/v2/clm/clm/contract-management-agreement.png`}
              alt="custom-view"
              width={300}
              height={300}
              className="w-full h-full rounded-2xl"
            />
            <div className="text-start">
              <p className="text-xl md:text-2xl">
                Set custom approval flows, clickwrap agreements, and get deals
                done faster
              </p>
            </div>
          </div>
        </div>
      </section>

      <div ref={sectionRef}>
        <EqualDivisionSection
          // title="Templates"
          heading="Unlock insights from every contract"
          description="Turn your contracts into a source of truth with real-time dashboards, actionable data, and smarter workflows—all in BoloSign."
          image={`${BASE_ASSETS_URL}/v2/clm/clm/contract-management-unlock.gif`}
          bgColor={isInView ? "bg-dark" : "bg-transparent"}
          imageRight={true}
          checks={[
            "Visualize the Full Lifecycle – Track every stage from draft to signature with 30+ real-time metrics and turnaround time insights.",
            "Optimize Workflows with Data – Identify bottlenecks, improve SLAs, and streamline high-volume contracts like NDAs and MSAs.",
            "Align Legal and Business Teams – Share clear reports and success metrics to drive collaboration and showcase legal’s impact.",
          ]}
        />
      </div>

      <EqualDivisionSection
        // title="Templates"
        heading="See what your contracts are really telling you"
        description="BoloSign turns contract data into powerful insights—so you can search smarter, report faster, and optimize every workflow with ease."
        image={`${BASE_ASSETS_URL}/v2/clm/clm/contract-management-see.gif`}
        bgColor="bg-transparent"
        imageRight={false}
        checks={[
          "Smart Search & Filters – Find contracts by terms, dates, revenue, or parties; save custom views that auto-update as new contracts arrive.",
          "Automated Reports – Schedule recurring reports from saved views and share them across departments for better decision-making.",
          "Workflow Insights – Identify bottlenecks, track team workloads, and improve turnaround times with real-time, actionable analytics.",
        ]}
      />

      <EqualDivisionSection
        // title="Templates"
        heading="Centralize every contract in one smart hub"
        description="BoloSign gives you a secure, AI-powered space to store, search, and manage contracts—making it easy to access insights, stay compliant, and work faster."
        image={`${BASE_ASSETS_URL}/v2/clm/clm/contract-management-centralize.gif`}
        bgColor="bg-cream"
        imageRight={true}
        checks={[
          "Find What You Need, Fast – Use full-text search, smart filters, and saved views to instantly locate any contract.",
          "Automate & Enrich Data – Auto-extract metadata, link related docs, and generate reports without manual effort.",
          "Stay Secure & Compliant – Protect sensitive data with encryption, role-based access, and detailed audit logs.",
        ]}
      />

      <EqualDivisionSection
        // title="Templates"
        heading="Secure, trackable clickwraps for high-scale workflows"
        description="BoloSign makes it easy to embed, manage, and track user agreements—ensuring legal proof, version control, and full visibility for your team."
        // image={`${BASE_ASSETS_URL}/v2/clm/clm/contract-management-secure-1.gif")}
        video={`${BASE_ASSETS_URL}/v2/clm/videos/contract-management-secure-1.mp4`}
        bgColor="bg-transparent"
        imageRight={false}
        checks={[
          "Embed in Minutes – Add clickwraps to your site with a simple snippet—no dev time or complex setup required.",
          "Capture Legally Enforceable Consent – Track every acceptance with time, IP, and identity for audit-ready records.",
          "Centralize & Manage Versions – Auto-update terms, track who accepted what, and give legal full oversight from one hub.",
        ]}
      />

      <EqualDivisionSection
        // title="Templates"
        heading="Automate every step of your contract process"
        description="From drafting to approvals and signatures, BoloSign lets you build error-free, no-code workflows that move contracts faster—right inside your favorite tools."
        image={`${BASE_ASSETS_URL}/v2/clm/clm/contract-management-msa.gif`}
        bgColor="bg-dark"
        imageRight={true}
        checks={[
          "Generate Contracts Instantly – Auto-fill templates with form data to create accurate, ready-to-send contracts—no edits needed.",
          "Work From Your Existing Tools – Create, send, and track contracts directly from platforms like Airtable, HubSpot, and Pipedrive.",
          "Streamline Approvals & Reviews – Use smart routing, intake forms, and real-time tracking to eliminate delays and manual follow-ups.",
        ]}
      />

      <section className="px-0 md:px-8  bg-cream">
        <div className=" max-w-screen-xl mx-auto px-6 xl:px-8 md:py-32 py-16 text-black md:rounded-2xl flex flex-col md:flex-row justify-between ">
          <div className="w-full  mb-6 md:mb-0 ">
            <h2 className="text-2xl md:text-4xl xl:text-5xl font-semibold mb-4 xl:mb-8">
              Here’s how Shine Above uses BoloSign CLM
            </h2>
            <Image
              src={`${BASE_ASSETS_URL}/v2/clm/clm/contract-management-feedback.png`}
              alt="Customer discussion"
              className="w-[90%]"
              width={500}
              height={500}
            />
          </div>

          <div className="w-full md:w-2/3 ">
            <Image
              src={`${BASE_ASSETS_URL}/v2/clm/clm/contract-management-brown.png`}
              alt="Customer discussion"
              className="w-full"
              width={500}
              height={500}
            />
          </div>
        </div>
      </section>
    </>
  );
};

export default ContractManagement;
